import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

const IconButton = React.forwardRef(
  (
    {
      children,
      variant = 'ghost',
      size = 'md',
      disabled = false,
      loading = false,
      'aria-label': ariaLabel,
      onClick,
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    const { semantic, controlHeights, radiusPx } = useFeatureTheme();

    const sizes = {
      sm: controlHeights.sm,
      md: controlHeights.md,
      lg: controlHeights.lg,
    };

    const variants = {
      ghost: {
        bgcolor: 'transparent',
        color: semantic.text.primary,
        border: 'none',
        '&:hover:not(:disabled)': { bgcolor: semantic.surfaces.surfaceMuted },
      },
      soft: {
        bgcolor: semantic.surfaces.surfaceMuted,
        color: semantic.text.primary,
        border: `1px solid ${semantic.borders.border}`,
        '&:hover:not(:disabled)': { bgcolor: semantic.surfaces.surface },
      },
      primary: {
        bgcolor: semantic.interactive.primary,
        color: semantic.interactive.primaryForeground,
        border: `1px solid ${semantic.interactive.primary}`,
        '&:hover:not(:disabled)': { bgcolor: semantic.interactive.primaryHover },
      },
    };

    const dimension = sizes[size] || sizes.md;

    return (
      <Box
        ref={ref}
        component="button"
        type="button"
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        onClick={onClick}
        className={className}
        sx={{
          ...variants[variant],
          width: dimension,
          height: dimension,
          minWidth: dimension,
          borderRadius: radiusPx.md,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 160ms ease',
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 3px ${semantic.borders.focusRing}`,
          },
          ...sx,
        }}
        {...props}
      >
        {loading ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : children}
      </Box>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
